import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { ScrollView, type ScrollViewProps } from 'react-native';
import type { KeyboardAwareScrollViewProps } from 'react-native-keyboard-controller';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useFont } from '@/providers/FontProvider';
import { useLocale } from '@/hooks/useLocale';

interface Props {
  visible: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  centerHeader?: boolean;
  headerAction?: 'close' | 'back';
  children: React.ReactNode;
  footer?: React.ReactNode;
  onAfterClose?: () => void;
}

/** Match the native sheet's scroll exports for shared form and CV content. */
export const BottomSheetScrollView = React.forwardRef<ScrollView, ScrollViewProps>(
  function BottomSheetScrollView({ style, ...props }, ref) {
    return (
      <ScrollView
        {...props}
        ref={ref}
        style={[{ flexShrink: 1, minHeight: 0 }, style]}
      />
    );
  },
);

export const BottomSheetKeyboardAwareScrollView = React.forwardRef<
  ScrollView,
  KeyboardAwareScrollViewProps
>(function BottomSheetKeyboardAwareScrollView({ bottomOffset: _bottomOffset, ...props }, ref) {
  return <BottomSheetScrollView {...(props as ScrollViewProps)} ref={ref} />;
});

export function BottomSheet({
  visible,
  onClose,
  title,
  subtitle,
  centerHeader = true,
  headerAction = 'back',
  children,
  footer,
  onAfterClose,
}: Props) {
  const { theme, isDark } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const wasVisibleRef = useRef(false);

  useEffect(() => {
    if (visible) {
      wasVisibleRef.current = true;
      return;
    }
    if (wasVisibleRef.current) {
      wasVisibleRef.current = false;
      onAfterClose?.();
    }
  }, [onAfterClose, visible]);

  useEffect(() => {
    if (!visible || typeof document === 'undefined') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, visible]);

  if (!visible || typeof document === 'undefined') {
    return null;
  }

  const textAlign = centerHeader ? 'center' : isRTL ? 'right' : 'left';
  const titleColor = isDark ? theme.text : '#525252';
  const borderColor = isDark ? theme.border : 'rgba(0,0,0,0.1)';
  const sheetBackground = isDark ? '#2d2e31' : theme.background;
  const sheetBorderColor = isDark ? 'rgba(255,255,255,0.1)' : 'transparent';
  const isBackAction = headerAction === 'back';
  const HeaderIcon = isBackAction ? (isRTL ? ChevronRight : ChevronLeft) : X;

  return createPortal(
    <div
      role="dialog"
      dir={isRTL ? 'rtl' : 'ltr'}
      aria-modal="true"
      aria-label={title}
      style={overlayStyle}
    >
      <button
        type="button"
        aria-label={isRTL ? 'إغلاق' : 'Close'}
        onClick={onClose}
        style={backdropStyle}
      />
      <div
        style={{
          ...sheetStyle,
          backgroundColor: sheetBackground,
          borderColor: sheetBorderColor,
          boxShadow: isDark
            ? '0 -18px 54px rgba(0,0,0,0.42)'
            : '0 -18px 54px rgba(0,0,0,0.12)',
          color: theme.text,
        }}
      >
        <div
          style={{
            ...handleStyle,
            backgroundColor: isDark ? theme.borderStrong : '#ebebeb',
          }}
        />
        {title ? (
          <div
            style={{
              ...headerStyle,
              flexDirection: 'row',
              justifyContent: centerHeader ? 'center' : 'space-between',
            }}
          >
            <div
              style={{
                ...headerTextStyle,
                alignItems: centerHeader ? 'center' : 'stretch',
              }}
            >
              <h2
                style={{
                  ...titleStyle,
                  color: titleColor,
                  fontFamily: fonts.medium,
                  textAlign,
                }}
              >
                {title}
              </h2>
              {subtitle ? (
                <p
                  style={{
                    ...subtitleStyle,
                    color: theme.textTertiary,
                    fontFamily: fonts.regular,
                    textAlign,
                  }}
                >
                  {subtitle}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={isBackAction ? (isRTL ? 'رجوع' : 'Back') : (isRTL ? 'إغلاق' : 'Close')}
              style={{
                ...closeButtonStyle,
                ...(centerHeader
                  ? isBackAction
                    ? backButtonAbsoluteStyle
                    : closeButtonAbsoluteStyle
                  : null),
                ...(isBackAction ? backButtonStyle : null),
                borderColor: isBackAction ? 'transparent' : borderColor,
                color: isBackAction ? titleColor : theme.text,
              }}
            >
              <HeaderIcon size={isBackAction ? 24 : 18} strokeWidth={2} />
            </button>
          </div>
        ) : null}
        <div style={contentStyle}>{children}</div>
        {footer ? <div style={footerStyle}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const backdropStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  border: 'none',
  padding: 0,
  margin: 0,
  background: 'rgba(0,0,0,0.4)',
  cursor: 'default',
};

const sheetStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 480,
  maxHeight: 'calc(100vh - 32px)',
  overflowY: 'auto',
  borderTopLeftRadius: 55,
  borderTopRightRadius: 55,
  borderWidth: 1,
  borderStyle: 'solid',
  borderBottomWidth: 0,
  padding: '12px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  boxSizing: 'border-box',
};

const handleStyle: CSSProperties = {
  width: 52,
  height: 6,
  borderRadius: 40,
  alignSelf: 'center',
  flexShrink: 0,
};

const headerStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: 35,
};

const headerTextStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 20,
  fontWeight: 500,
  letterSpacing: -0.2,
  lineHeight: '26px',
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: '20px',
  opacity: 0.6,
};

const closeButtonStyle: CSSProperties = {
  width: 35,
  height: 35,
  borderRadius: 18,
  borderWidth: 1,
  borderStyle: 'solid',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const backButtonStyle: CSSProperties = {
  borderWidth: 0,
  borderRadius: 0,
};

const closeButtonAbsoluteStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  insetInlineEnd: 0,
};

const backButtonAbsoluteStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  insetInlineStart: 0,
};

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const footerStyle: CSSProperties = {
  paddingTop: 4,
  // Flex column so a `block` NativeButton's alignSelf:'stretch' actually
  // stretches. As a plain block container the button laid out at its intrinsic
  // width and hugged the start edge.
  display: 'flex',
  flexDirection: 'column',
};
